import React from "react";

import { faCopy, faCut, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import { faUpRightFromSquare } from "@fortawesome/pro-solid-svg-icons";
import { observer } from "mobx-react-lite";

import { useVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { ResourceType } from "src/clients/flow-api";
import { toastActions } from "src/design-system/Toast/utils";
import { Toolbar } from "src/design-system/Toolbar";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { getCreatableNodes } from "src/nodeAdding/PasteNodesCard";
import { useGetChildVersionLink } from "src/parentFlowNodes/useGetChildVersionLink";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { AuthorPageParams } from "src/router/urls";
import { useGraphStore } from "src/store/StoreProvider";
import { Subgraph } from "src/utils/GraphUtils";
import { isParentNode } from "src/utils/predicates";
import { pluralize } from "src/utils/stringUtils";
import { useParamsDecode } from "src/utils/useParamsDecode";

type Props = {
  onMouseEnter: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave: React.MouseEventHandler<HTMLDivElement>;
  id: string;
  close: () => void;
};

export const getCopySuccessMessage = (
  clipboard: Subgraph,
  isCutting?: boolean,
) =>
  `${pluralize(getCreatableNodes(clipboard.nodes).length, "node")} ${
    clipboard.groups.length > 0
      ? `and ${pluralize(clipboard.groups.length, "group")} `
      : ""
  }${isCutting ? "cut" : "copied"}`;

export const NodeControls: React.FC<Props> = observer(
  ({ onMouseEnter, onMouseLeave, close, id }) => {
    const {
      setNodeToDelete,
      setIsDeleteNodeModalVisible,
      copyToClipboard,
      nodes,
      deleteNode,
    } = useGraphStore();
    const { wsId } = useParamsDecode<AuthorPageParams>();
    const { orgId, workspace } = useAuthoringContext();
    const getChildVersionLink = useGetChildVersionLink();

    const { lockedByOtherUser } = useVersionResourceLock(ResourceType.NODE, id);
    const canModifyGraph = useCanAuthoringEditFlowVersion();

    const onClickDeleteButton = () => {
      setNodeToDelete(id);
      close();
      setIsDeleteNodeModalVisible(true);
    };

    const handleCopyNodeToClipboard = async (isCutting?: boolean) => {
      const node = nodes.get(id);
      if (node) {
        const copiedClipboard = await copyToClipboard([node], wsId);
        if (copiedClipboard) {
          if (isCutting) {
            try {
              await deleteNode(node, true);
            } catch {
              return copiedClipboard;
            }
          }
          toastActions.success({
            title: getCopySuccessMessage(copiedClipboard, isCutting),
          });
        } else {
          toastActions.failure({
            title: "Copy to clipboard failed",
            description: "See the documentation for more information",
          });
        }
      }
    };

    const node = nodes.get(id);

    return (
      <div
        className="absolute top-0 -right-2.5 translate-x-full"
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <Toolbar onClick={(e) => e.stopPropagation()}>
          {node &&
            isParentNode(node) &&
            node.data.child_flow_id &&
            node.data.child_flow_version_id && (
              <Toolbar.IconLink
                openInNewTab
                dataLoc="child-flow-link"
                href={getChildVersionLink({
                  orgId,
                  workspaceId: workspace.id,
                  flowId: node.data.child_flow_id,
                  versionId: node.data.child_flow_version_id,
                })}
                icon={faUpRightFromSquare}
                tooltip="Open in new tab"
              />
            )}
          {canModifyGraph && !lockedByOtherUser && (
            <Toolbar.IconButton
              dataLoc="cut-node-button"
              icon={faCut}
              tooltip="Cut"
              onClick={() => handleCopyNodeToClipboard(true)}
            />
          )}
          {!lockedByOtherUser && (
            <Toolbar.IconButton
              dataLoc="copy-node-button"
              icon={faCopy}
              tooltip="Copy"
              onClick={() => handleCopyNodeToClipboard()}
            />
          )}
          {canModifyGraph && !lockedByOtherUser && (
            <Toolbar.IconButton
              dataLoc="delete-node-button"
              icon={faTrashCan}
              tooltip="Delete"
              onClick={onClickDeleteButton}
            />
          )}
        </Toolbar>
      </div>
    );
  },
);
